/**
 * The send composer's chip-and-type recipient field — the machinery To and Cc
 * share (Cc added on client request 2026-08-06).
 *
 * WHAT IS SHARED IS THE FIELD, NOT THE LIST BEHIND IT. Both fields are a row
 * of removable chips followed by a Gmail-style inline input with a suggestion
 * popover, and that really is one control: the chips, the keyboard rules, the
 * popover, the aria wiring, the hint line under it. What differs is what a
 * chip MEANS, and none of that moved in here:
 *
 *   - **To carries roster semantics.** Its chips are the configured primary +
 *     the additional-recipients roster, held in a checks map, and typing a
 *     removed roster address back re-activates its check rather than
 *     duplicating it as a one-off. The composer still owns every bit of that;
 *     this file only reports "the PM committed this address" and lets the
 *     caller decide what that means.
 *   - **Cc has no roster.** It starts empty on every send and holds only what
 *     the PM put there for that send.
 *
 * So: the CALLER owns the list (what a chip is, what removing one does, what
 * committing one does), the HOOK owns the input (what is typed, whether it is
 * valid, whether it is already in the message, which suggestion is
 * highlighted), and the COMPONENT owns the paint.
 */
import { useCallback, useId, useRef, useState } from 'react';
import type {
  ChangeEvent as ReactChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';
import { filterRecipientSuggestions, nextActiveIndex } from '../lib/recipientSuggestions';

/**
 * Same loose email shape the server accepts (settings/recipients.ts EMAIL_RE).
 * Deliberately NOT exported: a non-component export from this file breaks
 * Fast Refresh for it (vite-plugin-react invalidates the whole module), and
 * nothing outside needs it.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface RecipientChipInput {
  typed: string;
  typedTrimmed: string;
  typedValid: boolean;
  /** Something is typed and it isn't an address — the only state that blocks Send. */
  typedShowsError: boolean;
  typedIsDuplicate: boolean;
  /**
   * A valid, non-duplicate address still sitting in the input. It IS a
   * recipient without being committed to a chip first — deliberately the old
   * one-off field's behavior: the PM types an address, clicks Send, and it
   * goes. Committing on blur instead would race the click that caused the blur.
   */
  pendingTyped: string | null;
  suggestOpen: boolean;
  activeIdx: number;
  suggestVisible: boolean;
  suggestions: readonly string[];
  disabled: boolean;
  /** Clear the typed value and close the popover (the modal's per-open reset). */
  reset: () => void;
  /** Commit an address: hands it to `onCommit`, clears the input, refocuses. */
  addChip: (email: string) => void;
  setActiveIdx: (i: number) => void;
  onChange: (e: ReactChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
}

export function useRecipientChipInput(args: {
  /** Contact book, most-recently-used first (the suggestion pool's first arm). */
  contacts: readonly string[];
  /** Configured primary + roster (the pool's second arm). */
  roster: readonly string[];
  /**
   * Every address already on the message — BOTH fields' chips. Removed from
   * the suggestion pool, because offering an address the email already reaches
   * is offering nothing.
   */
  onMessage: readonly string[];
  /**
   * THIS field's own chips. What "already in the list" means for the hint, and
   * deliberately narrower than `onMessage`: an address sitting in Cc is not a
   * duplicate of the To field, it is a candidate to move there.
   */
  chipped: readonly string[];
  /** True while the send is in flight — the whole field is inert. */
  disabled: boolean;
  /** The PM committed an address. The caller decides where it lands. */
  onCommit: (email: string) => void;
  /**
   * Backspace on an empty input drops the last chip — Gmail's behavior, scoped
   * by the caller to the chips it considers droppable that way. `canBackspace`
   * false makes the key inert (and, as before, does NOT preventDefault).
   */
  canBackspace?: boolean;
  onBackspaceEmpty?: () => void;
}): RecipientChipInput {
  const { chipped, disabled, onCommit } = args;
  const [typed, setTyped] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const typedTrimmed = typed.trim();
  const typedValid = typedTrimmed !== '' && EMAIL_RE.test(typedTrimmed);
  const typedShowsError = typedTrimmed !== '' && !typedValid;
  // De-dupe against `chipped` — THIS FIELD's own chips, not the whole message
  // — so a re-typed address can't double-count toward its total. The caller
  // decides how wide that is, and the two fields answer differently: To passes
  // its own chips (an address in Cc is a candidate to move up, not a
  // duplicate), Cc passes the whole message (an address already addressed in
  // To is not something to also copy). `onMessage`, which IS the whole
  // message, only ever narrows the suggestion pool below.
  const typedIsDuplicate =
    typedValid && chipped.some((e) => e.toLowerCase() === typedTrimmed.toLowerCase());
  const pendingTyped = typedValid && !typedIsDuplicate ? typedTrimmed : null;

  const suggestions = filterRecipientSuggestions({
    contacts: args.contacts,
    roster: args.roster,
    chipped: args.onMessage,
    query: typed,
  });
  const suggestVisible = suggestOpen && !disabled && suggestions.length > 0;

  // Referentially STABLE (state setters are), so the composer's per-open reset
  // effect can list it as a dependency and still fire only when the modal
  // opens or the send target changes. An unstable reset there would re-run the
  // effect on every render and wipe the PM's edits mid-compose.
  const reset = useCallback(() => {
    setTyped('');
    setSuggestOpen(false);
    setActiveIdx(-1);
  }, []);

  /** Chip an address and clear the input. Duplicates are the caller's to
   *  ignore — the suggestion list already excludes them, so `onCommit` only
   *  ever sees a free-typed repeat. */
  const addChip = (email: string) => {
    const value = email.trim();
    if (value === '') return;
    onCommit(value);
    setTyped('');
    setSuggestOpen(false);
    setActiveIdx(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setSuggestOpen(true);
      setActiveIdx((i) =>
        nextActiveIndex(suggestOpen ? i : -1, e.key === 'ArrowDown' ? 1 : -1, suggestions.length),
      );
      return;
    }
    if (e.key === 'Enter') {
      // Never let Enter reach a form or the modal — it either takes a
      // suggestion, chips what was typed, or does nothing.
      e.preventDefault();
      if (suggestVisible && activeIdx >= 0 && suggestions[activeIdx]) {
        addChip(suggestions[activeIdx]!);
      } else if (typedValid) {
        addChip(typedTrimmed);
      }
      return;
    }
    if (e.key === 'Escape') {
      // Escape closes the suggestions FIRST. Only when they're already shut
      // does it fall through to the window listener that closes the modal —
      // hence stopPropagation only in the open case.
      if (suggestVisible) {
        e.stopPropagation();
        setSuggestOpen(false);
        setActiveIdx(-1);
      }
      return;
    }
    if (e.key === 'Backspace' && typed === '' && args.canBackspace) {
      e.preventDefault();
      args.onBackspaceEmpty?.();
    }
  };

  return {
    typed,
    typedTrimmed,
    typedValid,
    typedShowsError,
    typedIsDuplicate,
    pendingTyped,
    suggestOpen,
    activeIdx,
    suggestVisible,
    suggestions,
    disabled,
    reset,
    addChip,
    setActiveIdx,
    onChange: (e) => {
      setTyped(e.target.value);
      setSuggestOpen(true);
      setActiveIdx(-1);
    },
    onKeyDown,
    onBlur: () => {
      setSuggestOpen(false);
      setActiveIdx(-1);
    },
    inputRef,
  };
}

/**
 * One labeled recipient field: the section label, an optional note under it,
 * the chip row with its inline input and suggestion popover, and the hint.
 *
 * A field with no chips renders exactly the same box holding just the input —
 * which is what makes an empty Cc read as an empty field rather than as
 * something missing.
 */
export function RecipientChipField({
  label,
  idSuffix,
  chips,
  onRemoveChip,
  input,
  placeholder,
  addAriaLabel,
  note,
  hint,
}: {
  /** "To" / "Cc" — uppercased by `.composer-section-label`. */
  label: string;
  /** Distinguishes this field's listbox id from its sibling's. */
  idSuffix: string;
  /** Addresses currently on this field, in render order. */
  chips: readonly string[];
  onRemoveChip: (email: string) => void;
  input: RecipientChipInput;
  placeholder: string;
  addAriaLabel: string;
  /** Rendered between the label and the field (To's "no GC email" line). */
  note?: ReactNode;
  hint?: ReactNode;
}) {
  const suggestListId = `${useId()}-${idSuffix}-suggestions`;
  return (
    <div className="composer-section">
      <div className="composer-section-label">{label}</div>
      {note}
      <div className="composer-to-field">
        {chips.map((email) => (
          <button
            key={email}
            type="button"
            className="composer-chip"
            disabled={input.disabled}
            title="Remove from this send"
            aria-label={`Remove ${email} from this send`}
            onClick={() => onRemoveChip(email)}
          >
            <span className="mono">{email}</span>
            <span aria-hidden className="composer-chip-x">
              ×
            </span>
          </button>
        ))}
        <div className="composer-to-input-wrap">
          <input
            ref={input.inputRef}
            type="text"
            className="composer-to-input mono"
            value={input.typed}
            disabled={input.disabled}
            maxLength={320}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            onChange={input.onChange}
            onKeyDown={input.onKeyDown}
            // The popover's rows use onMouseDown + preventDefault, so a
            // click on one never blurs the input and never reaches this.
            onBlur={input.onBlur}
            role="combobox"
            aria-expanded={input.suggestVisible}
            // Only while the listbox is actually in the DOM — a dangling
            // aria-controls is an invalid value, not a hint.
            aria-controls={input.suggestVisible ? suggestListId : undefined}
            aria-activedescendant={
              input.suggestVisible && input.activeIdx >= 0
                ? `${suggestListId}-opt-${input.activeIdx}`
                : undefined
            }
            aria-autocomplete="list"
            aria-invalid={input.typedShowsError}
            aria-label={addAriaLabel}
          />
          {input.suggestVisible && (
            <ul
              className="dropdown-menu dropdown-menu--sm composer-suggest"
              id={suggestListId}
              role="listbox"
              aria-label="Recipient suggestions"
            >
              {input.suggestions.map((email, i) => (
                <li
                  key={email}
                  id={`${suggestListId}-opt-${i}`}
                  role="option"
                  aria-selected={i === input.activeIdx}
                  className={`dropdown-opt${i === input.activeIdx ? ' is-active' : ''}`}
                  // mousedown, not click: it fires before blur, and
                  // preventDefault keeps focus in the input so the
                  // popover isn't torn down mid-selection.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    input.addChip(email);
                  }}
                  onMouseEnter={() => input.setActiveIdx(i)}
                >
                  <span className="dropdown-opt-label mono">{email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {hint}
    </div>
  );
}
